import React, { useState, useRef, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import PersonalDetails from "features/Auth/v2/Signup/SurmountRecommendations/PersonalDetails";
import PhoneVerification from "features/Auth/v2/Signup/SurmountRecommendations/PhoneVerification";
import KycQuestions from "features/Auth/v2/Signup/SurmountRecommendations/KycQuestions";
import IntroQuestions from "features/Auth/v2/Signup/SurmountRecommendations/IntroQuestions";
import SurmountAccountTaxW9Agreement from "features/Auth/v2/Signup/SetupFunding/SurmountAccount/TaxW9Agreement";
import { IconClose, PrismCoinIcon, AllSetICon } from "assets/icons";
import {
  CreateSurmountAccountModalContainer,
  ModalHeader,
  TabsContainer,
  Tab,
  CloseButton,
} from "./CreateSurmountAccountModal.styles";
import { Text } from "style/v2/components/Typography";
import { useNavigationInterceptor } from "shared/hooks/useNavigationInterceptor";
import { useNavigation } from "utils/navigate";
import StatusScreen from "./StatusScreen";

const STEPS = {
  PERSONAL_DETAILS: 1,
  INVESTMENT_GOALS: 2,
  REVIEW_SUBMIT: 3,
  PROCESSING: 4,
  ALL_SET: 5,
};

const PERSONAL_DETAILS_SUB_STEPS = {
  FORM: "form",
  PHONE_VERIFICATION: "phone_verification",
  KYC_QUESTIONS: "kyc_questions",
};

// Route keys as enum-like constants for type safety and reusability
export const ROUTE_KEYS = {
  PHONE_VERIFICATION: "PHONE_VERIFICATION",
  PERSONAL_DETAILS: "PERSONAL_DETAILS",
  KYC_QUESTIONS: "KYC_QUESTIONS",
  INTRO_QUESTIONS: "INTRO_QUESTIONS",
  TAX_W9: "TAX_W9",
  FUND_ACCOUNT: "FUND_ACCOUNT",
};

// Route patterns for navigation interception
// Using RegExp patterns for more precise and robust matching
// Patterns use .* to allow query params and trailing slashes for flexibility
// Centralized here to avoid tight coupling and make maintenance easier
const ROUTE_PATTERNS = {
  [ROUTE_KEYS.PHONE_VERIFICATION]: [
    /phone-verification.*$/,
    /\/signup\/surmount-recommendations\/phone-verification.*$/,
  ],
  [ROUTE_KEYS.PERSONAL_DETAILS]: [
    /personal-details.*$/,
    /funding-method.*$/,
    /\/signup\/surmount-recommendations\/personal-details.*$/,
    /\/signup\/setup-funding\/surmount-account\/personal-details.*$/,
  ],
  [ROUTE_KEYS.KYC_QUESTIONS]: [
    /kyc-questions.*$/,
    /\/signup\/surmount-recommendations\/kyc-questions.*$/,
  ],
  [ROUTE_KEYS.INTRO_QUESTIONS]: [
    /intro-questions.*$/,
    /\/signup\/intro-questions.*$/,
    /\/signup\/surmount-recommendations\/intro.*$/,
    /surmount-recommendations\/intro.*$/,
  ],
  [ROUTE_KEYS.TAX_W9]: [
    /tax-w9(-agreement)?.*$/,
    /\/signup\/surmount-account\/tax-w9-agreement.*$/,
    /\/signup\/setup-funding\/surmount-account\/tax-w9-agreement.*$/,
  ],
  [ROUTE_KEYS.FUND_ACCOUNT]: [/\/signup\/fund-your-account.*$/],
};

// Wrapper component for PersonalDetails that intercepts navigation
const PersonalDetailsWrapper = ({ onSuccess, onBack }) => {
  useNavigationInterceptor({
    interceptPatterns: ROUTE_PATTERNS[ROUTE_KEYS.PHONE_VERIFICATION],
    onIntercept: onSuccess,
    enabled: !!onSuccess,
  });

  // Intercept funding-method navigation (used by "Go Back" button)
  useNavigationInterceptor({
    interceptPatterns: [
      /funding-method.*$/,
      /\/signup\/.*choose-funding-method.*$/,
      /\/signup\/setup-funding\/.*choose-funding-method.*$/,
    ],
    onIntercept: () => {
      if (onBack) {
        onBack();
      }
    },
    enabled: !!onBack,
  });

  return <PersonalDetails />;
};

PersonalDetailsWrapper.propTypes = {
  onSuccess: PropTypes.func.isRequired,
  onBack: PropTypes.func,
};

// Wrapper component for PhoneVerification that intercepts navigation
const PhoneVerificationWrapper = ({ onBack, onSuccess }) => {
  const successTriggered = useRef(false);

  useNavigationInterceptor({
    interceptPatterns: ROUTE_PATTERNS[ROUTE_KEYS.PERSONAL_DETAILS],
    onIntercept: () => {
      onBack();
    },
    enabled: !!onBack,
  });

  useNavigationInterceptor({
    interceptPatterns: ROUTE_PATTERNS[ROUTE_KEYS.KYC_QUESTIONS],
    onIntercept: () => {
      if (!successTriggered.current && onSuccess) {
        successTriggered.current = true;
        onSuccess();
      }
    },
    enabled: !!onSuccess,
  });

  return <PhoneVerification />;
};

PhoneVerificationWrapper.propTypes = {
  onBack: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};

// Wrapper component for KycQuestions that intercepts navigation
const KycQuestionsWrapper = ({ onBack, onSuccess }) => {
  const successTriggered = useRef(false);

  useNavigationInterceptor({
    interceptPatterns: ROUTE_PATTERNS[ROUTE_KEYS.PHONE_VERIFICATION],
    onIntercept: () => {
      onBack();
    },
    enabled: !!onBack,
  });

  useNavigationInterceptor({
    interceptPatterns: ROUTE_PATTERNS[ROUTE_KEYS.INTRO_QUESTIONS],
    onIntercept: () => {
      if (!successTriggered.current && onSuccess) {
        successTriggered.current = true;
        onSuccess();
      }
    },
    enabled: !!onSuccess,
  });

  return <KycQuestions />;
};

KycQuestionsWrapper.propTypes = {
  onBack: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};

// Wrapper component for IntroQuestions that intercepts navigation
const IntroQuestionsWrapper = ({ onBack, onSuccess }) => {
  const successTriggered = useRef(false);

  useNavigationInterceptor({
    interceptPatterns: [
      ...ROUTE_PATTERNS[ROUTE_KEYS.PHONE_VERIFICATION],
      ...ROUTE_PATTERNS[ROUTE_KEYS.PERSONAL_DETAILS],
      ...ROUTE_PATTERNS[ROUTE_KEYS.KYC_QUESTIONS],
    ],
    onIntercept: () => {
      onBack();
    },
    enabled: !!onBack,
  });

  useNavigationInterceptor({
    interceptPatterns: ROUTE_PATTERNS[ROUTE_KEYS.TAX_W9],
    onIntercept: () => {
      if (!successTriggered.current && onSuccess) {
        successTriggered.current = true;
        onSuccess();
      }
    },
    enabled: !!onSuccess,
  });

  return <IntroQuestions />;
};

IntroQuestionsWrapper.propTypes = {
  onBack: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};

// Wrapper component for TaxW9Agreement that intercepts navigation
const TaxW9AgreementWrapper = ({ onBack, onSuccess }) => {
  const successTriggered = useRef(false);

  useNavigationInterceptor({
    interceptPatterns: ROUTE_PATTERNS[ROUTE_KEYS.INTRO_QUESTIONS],
    onIntercept: () => {
      onBack();
    },
    enabled: !!onBack,
  });

  useNavigationInterceptor({
    interceptPatterns: ROUTE_PATTERNS[ROUTE_KEYS.FUND_ACCOUNT],
    onIntercept: () => {
      if (!successTriggered.current) {
        successTriggered.current = true;
        if (onSuccess) {
          onSuccess();
        }
      }
    },
    enabled: !!onSuccess,
  });

  return (
    <SurmountAccountTaxW9Agreement
      onSuccess={onSuccess}
      showStatusScreens={false}
    />
  );
};

TaxW9AgreementWrapper.propTypes = {
  onBack: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};

export const CreateSurmountAccountModal = ({ show, onHide }) => {
  const { navigateToDashboard } = useNavigation();
  const [activeStep, setActiveStep] = useState(STEPS.PERSONAL_DETAILS);
  const [personalDetailsSubStep, setPersonalDetailsSubStep] = useState(
    PERSONAL_DETAILS_SUB_STEPS.FORM
  );
  const [isPhoneVerified, setIsPhoneVerified] = useState(false);
  const [isInvestmentGoalsComplete, setIsInvestmentGoalsComplete] =
    useState(false);
  const processingTimeoutRef = useRef(null);
  const allSetCompleteRef = useRef(false);

  const clearTimers = () => {
    if (processingTimeoutRef.current) {
      clearTimeout(processingTimeoutRef.current);
      processingTimeoutRef.current = null;
    }
  };

  const clearValues = () => {
    clearTimers();
    setActiveStep(STEPS.PERSONAL_DETAILS);
    setPersonalDetailsSubStep(PERSONAL_DETAILS_SUB_STEPS.FORM);
    setIsPhoneVerified(false);
    setIsInvestmentGoalsComplete(false);
    allSetCompleteRef.current = false;
  };

  const handleModalClose = () => {
    clearValues();
    onHide();
  };

  const handlePersonalDetailsSuccess = () => {
    setPersonalDetailsSubStep(PERSONAL_DETAILS_SUB_STEPS.PHONE_VERIFICATION);
  };

  const handlePersonalDetailsBack = () => {
    handleModalClose();
  };

  const handlePhoneVerificationBack = () => {
    setPersonalDetailsSubStep(PERSONAL_DETAILS_SUB_STEPS.FORM);
  };

  const handlePhoneVerificationSuccess = () => {
    setIsPhoneVerified(true);
    setPersonalDetailsSubStep(PERSONAL_DETAILS_SUB_STEPS.KYC_QUESTIONS);
  };

  const handleKycQuestionsBack = () => {
    setPersonalDetailsSubStep(PERSONAL_DETAILS_SUB_STEPS.PHONE_VERIFICATION);
  };

  const handleKycQuestionsSuccess = () => {
    setActiveStep(STEPS.INVESTMENT_GOALS);
  };

  const handleInvestmentGoalsBack = () => {
    setActiveStep(STEPS.PERSONAL_DETAILS);
    setPersonalDetailsSubStep(PERSONAL_DETAILS_SUB_STEPS.KYC_QUESTIONS);
  };

  const handleInvestmentGoalsSuccess = () => {
    setIsInvestmentGoalsComplete(true);
    setActiveStep(STEPS.REVIEW_SUBMIT);
  };

  const handleReviewSubmitBack = () => {
    setActiveStep(STEPS.INVESTMENT_GOALS);
  };

  const handleReviewSubmitSuccess = () => {
    setActiveStep(STEPS.PROCESSING);
  };

  const handleProcessingContinue = () => {
    setActiveStep(STEPS.ALL_SET);
  };

  const handleAllSetContinue = () => {
    if (allSetCompleteRef.current) return;
    allSetCompleteRef.current = true;
    clearValues();
    onHide();
    navigateToDashboard();
  };

  useEffect(() => {
    clearTimers();

    if (!show) {
      return undefined;
    }

    if (activeStep === STEPS.PROCESSING) {
      processingTimeoutRef.current = setTimeout(() => {
        setActiveStep(STEPS.ALL_SET);
      }, 5000);
    }

    return () => {
      clearTimers();
    };
  }, [activeStep, show]);

  const tabs = useMemo(
    () => [
      { id: 1, label: "1. Personal details", step: STEPS.PERSONAL_DETAILS },
      {
        id: 2,
        label: "2. Investment goals",
        step: STEPS.INVESTMENT_GOALS,
        disabled: !isPhoneVerified,
      },
      {
        id: 3,
        label: "3. Review and submit",
        step: STEPS.REVIEW_SUBMIT,
        disabled: !isInvestmentGoalsComplete,
      },
    ],
    [isPhoneVerified, isInvestmentGoalsComplete]
  );

  const renderStepContent = () => {
    switch (activeStep) {
      case STEPS.PERSONAL_DETAILS:
        if (
          personalDetailsSubStep ===
          PERSONAL_DETAILS_SUB_STEPS.PHONE_VERIFICATION
        ) {
          return (
            <PhoneVerificationWrapper
              onBack={handlePhoneVerificationBack}
              onSuccess={handlePhoneVerificationSuccess}
            />
          );
        }
        if (
          personalDetailsSubStep === PERSONAL_DETAILS_SUB_STEPS.KYC_QUESTIONS
        ) {
          return (
            <KycQuestionsWrapper
              onBack={handleKycQuestionsBack}
              onSuccess={handleKycQuestionsSuccess}
            />
          );
        }
        return (
          <PersonalDetailsWrapper
            onSuccess={handlePersonalDetailsSuccess}
            onBack={handlePersonalDetailsBack}
          />
        );
      case STEPS.INVESTMENT_GOALS:
        return (
          <IntroQuestionsWrapper
            onBack={handleInvestmentGoalsBack}
            onSuccess={handleInvestmentGoalsSuccess}
          />
        );
      case STEPS.REVIEW_SUBMIT:
        return (
          <TaxW9AgreementWrapper
            onBack={handleReviewSubmitBack}
            onSuccess={handleReviewSubmitSuccess}
          />
        );
      case STEPS.PROCESSING:
        return (
          <StatusScreen
            variant="processing"
            icon={PrismCoinIcon}
            title="Processing Application..."
            description="We're processing your application. This might take 1-2 days."
            buttonLabel="Continue"
            onContinue={handleProcessingContinue}
          />
        );
      case STEPS.ALL_SET:
        return (
          <StatusScreen
            variant="allSet"
            icon={AllSetICon}
            title="You're all set"
            description="We're processing your application. This might take a minute."
            buttonLabel="Go to Dashboard"
            onContinue={handleAllSetContinue}
          />
        );
      default:
        return (
          <PersonalDetailsWrapper
            onSuccess={handlePersonalDetailsSuccess}
            onBack={handlePersonalDetailsBack}
          />
        );
    }
  };

  return (
    <CreateSurmountAccountModalContainer
      show={show}
      onHide={handleModalClose}
      size="xl"
      showClose={false}
    >
      {activeStep !== STEPS.PROCESSING && activeStep !== STEPS.ALL_SET && (
        <ModalHeader>
          <div>
            <Text variant="cardHeading" weight="400" as="h2" mb="20px">
              Apply for Surmount account
            </Text>
            <TabsContainer>
              {tabs.map((tab) => (
                <Tab
                  key={tab.id}
                  $active={activeStep === tab.step}
                  disabled={tab.disabled}
                  onClick={() =>
                    !tab.disabled && tab.step && setActiveStep(tab.step)
                  }
                >
                  {tab.label}
                </Tab>
              ))}
            </TabsContainer>
          </div>
          <CloseButton onClick={handleModalClose} aria-label="Close modal">
            <IconClose />
          </CloseButton>
        </ModalHeader>
      )}
      <div
        className="modal-content-wrapper"
        data-step={
          activeStep === STEPS.PERSONAL_DETAILS
            ? personalDetailsSubStep ===
              PERSONAL_DETAILS_SUB_STEPS.PHONE_VERIFICATION
              ? "PHONE_VERIFICATION"
              : personalDetailsSubStep ===
                PERSONAL_DETAILS_SUB_STEPS.KYC_QUESTIONS
              ? "KYC_QUESTIONS"
              : "PERSONAL_DETAILS"
            : activeStep === STEPS.INVESTMENT_GOALS
            ? "INVESTMENT_GOALS"
            : activeStep === STEPS.REVIEW_SUBMIT
            ? "REVIEW_SUBMIT"
            : null
        }
      >
        {renderStepContent()}
      </div>
    </CreateSurmountAccountModalContainer>
  );
};

CreateSurmountAccountModal.propTypes = {
  show: PropTypes.bool,
  onHide: PropTypes.func.isRequired,
};

CreateSurmountAccountModal.displayName = "CreateSurmountAccountModal";
